import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, FormHelperText, Grid, Link, Tooltip } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormProps } from 'util/Form';
import { TableColumn } from 'store/types/Table';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import Table from 'components/shared/Table';
import Add from '@mui/icons-material/Add';
import PreferencesService from 'services/api/PreferencesService';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import AddSublistItemModal from './AddSublistItemModal';
import QuestionType from 'store/enums/QuestionType';
import FileInfo from 'store/types/FileInfo';
import classNames from 'classnames';
import { getFormattedDate, NUMBER_DATE_FORMAT, truncateFilename } from 'util/Format';
import { getPrice } from 'util/Payment';
import { AuraAttributeOption, PreferenceQuestionView, SublistAnswer } from 'store/types/AuraAttribute';
import { ParametersContext } from 'components/ParametersGuard';

import commonStyles from 'styles/common.module.scss';

interface SublistQuestionFormItemProps extends FormProps {
  question: PreferenceQuestionView;
}

interface SublistAnswerView extends SublistAnswer {
  files?: FileInfo[];
  subtotal?: number;
}

interface SublistAnswerGroupView {
  referenceKey: string;
  answers: SublistAnswerView[];
  isSubtotal?: boolean;
}

const SublistQuestionFormItem: React.FunctionComponent<SublistQuestionFormItemProps> = ({ question, disabled }) => {
  const { sublistQuestions, fieldName } = question;
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const errorMessage = errors[fieldName]?.message;
  const {
    profile: { addNewSubListItemButtonName },
  } = useContext(ParametersContext);
  const [list, setList] = useState<SublistAnswerGroupView[]>([]);
  const [loading, setLoading] = useState<boolean>();
  const [displayResultRow, setDisplayResultRow] = useState<boolean>(false);
  const [addItemModalOpen, setAddItemModalOpen] = useState<boolean>(false);
  const sublistAnswersView: Array<SublistAnswerView[]> = useWatch({ control, name: fieldName });
  const dropdownQuestionOptions: AuraAttributeOption[] | undefined = useMemo(
    () => sublistQuestions?.find((item) => item.type?.name === QuestionType.Dropdown)?.options,
    [sublistQuestions]
  );

  useEffect(() => {
    setDisplayResultRow(
      !!sublistQuestions?.some(({ type }) => type.name === QuestionType.Currency && sublistAnswersView.length)
    );
  }, [sublistAnswersView, sublistQuestions]);

  useEffect(() => {
    const formatAnswer = (sublistAnswer: SublistAnswerView) => {
      const { type, sublistQuestionId, answer } = sublistAnswer;

      switch (type) {
        case QuestionType.Dropdown:
          return (
            question.sublistQuestions
              ?.find((sublistQuestion) => sublistQuestion.id === sublistQuestionId)
              ?.options?.find((option) => option.id === answer)?.option || ''
          );
        case QuestionType.Date:
          return getFormattedDate(answer, NUMBER_DATE_FORMAT);
        default:
          return answer;
      }
    };

    const groupedAnswers = new Map<string, SublistAnswerGroupView>();
    sublistAnswersView.forEach((row) =>
      row.forEach((sublistAnswer) => {
        const { referenceKey } = sublistAnswer;
        const files = question.answers?.find((answer) => sublistAnswer.answerId === answer.id)?.files;
        const stagedFiles = getValues(`sublistFiles.${referenceKey}`)?.files || [];
        const view: SublistAnswerView = {
          ...sublistAnswer,
          answer: formatAnswer(sublistAnswer),
          files: [...(files || []), ...stagedFiles],
        };

        const existing = groupedAnswers.get(referenceKey);
        if (existing) existing.answers.push(view);
        else groupedAnswers.set(referenceKey, { referenceKey, answers: [view] });
      })
    );

    const updatedList = Array.from(groupedAnswers.values());

    const subtotals: SublistAnswerGroupView = { referenceKey: '', answers: [], isSubtotal: true };
    updatedList.forEach((item) =>
      item.answers.forEach((answer) => {
        if (answer.type === QuestionType.Currency) {
          const currentSubtotal = subtotals.answers.find((sub) => sub.answer === answer.answer);
          if (currentSubtotal) currentSubtotal.subtotal = (currentSubtotal.subtotal || 0) + Number(answer.answer || 0);
          else
            subtotals.answers.push({
              ...answer,
              subtotal: Number(answer.answer || 0),
            });
        }
      })
    );

    if (subtotals.answers.length) updatedList.push(subtotals);

    setList(updatedList);
  }, [
    control,
    displayResultRow,
    dropdownQuestionOptions,
    fieldName,
    getValues,
    question.answers,
    question.sublistQuestions,
    sublistAnswersView,
    sublistQuestions,
  ]);

  const handleDeleteItem = useCallback(
    (record: SublistAnswerGroupView) => () => {
      const formValues = getValues();
      const newValues = fieldName
        .split('.')
        .reduce((nextObject: any, key: string) => nextObject[key], formValues)
        .filter((item: SublistAnswerView[]) => item[0].referenceKey !== record.referenceKey);
      setValue(fieldName, newValues);

      const sublistFiles = formValues['sublistFiles'];
      if (sublistFiles && sublistFiles[record.referenceKey]?.files) {
        sublistFiles[record.referenceKey].files = [];
      }
    },
    [fieldName, getValues, setValue]
  );

  const handleFileLinkClick = useCallback(
    (record: FileInfo) => () => {
      setLoading(true);
      PreferencesService.getAttributeFile(record)
        .then(() => {
          setLoading(false);
        })
        .catch((error: string) => {
          enqueueSnackbar(error, defaultSnackbarErrorProps);
          setLoading(false);
        });
    },
    [enqueueSnackbar]
  );

  const columns: Array<TableColumn<any>> = useMemo(() => {
    const result: Array<TableColumn<any>> = sublistQuestions?.length
      ? sublistQuestions.map(({ id, name, type }) => ({
          label: name,
          key: id,
          dataIndex: id,
          render: (_: any, record: SublistAnswerGroupView) => {
            const sublistAnswer = record.answers.find((item) => item.sublistQuestionId === id);

            switch (type?.name) {
              case QuestionType.Currency:
                return record.isSubtotal
                  ? getPrice(Number(sublistAnswer?.subtotal || 0))
                  : getPrice(Number(sublistAnswer?.answer || 0));

              case QuestionType.File:
                return (
                  <>
                    {sublistAnswer?.files?.map((file) => {
                      const fileName = truncateFilename(file.name, 20, 'end');
                      return (
                        <Tooltip key={file.name} title={file.name} placement="top-start">
                          {file.id ? (
                            <div>
                              <Link onClick={handleFileLinkClick(file)} className={commonStyles.link}>
                                {fileName}
                              </Link>
                            </div>
                          ) : (
                            <div>{fileName}</div>
                          )}
                        </Tooltip>
                      );
                    })}
                  </>
                );

              default:
                return sublistAnswer?.answer;
            }
          },
        }))
      : [];

    result.push({
      label: 'Action',
      key: 'action',
      render: (_: any, record: SublistAnswerGroupView) => (
        <Button
          size={'small'}
          variant={'outlined'}
          className={classNames(commonStyles.dangerButtonOutlined, { [commonStyles.hidden]: !!record.isSubtotal })}
          onClick={handleDeleteItem(record)}
          disabled={disabled}
        >
          <DeleteOutlined />
        </Button>
      ),
    });
    {
      displayResultRow &&
        result.unshift({
          label: '',
          key: 'result',
          dataIndex: 'result',
          render: (_: any, record: SublistAnswerGroupView) => <b>{record.isSubtotal && 'Subtotal'}</b>,
        });
    }

    return result;
  }, [disabled, displayResultRow, handleDeleteItem, handleFileLinkClick, sublistQuestions]);

  const handleAddItemModalOpen = useCallback(() => {
    setAddItemModalOpen(true);
  }, []);

  const handleAddItemModalClose = useCallback(() => {
    setAddItemModalOpen(false);
  }, []);

  return (
    <>
      <Grid {...defaultGridContainerProps} spacing={2}>
        <Grid {...defaultGridItemProps}>
          {errorMessage && <FormHelperText error={true}>{errorMessage}</FormHelperText>}
        </Grid>
        <Grid {...defaultGridItemProps}>
          <Button
            color={'secondary'}
            variant={'contained'}
            startIcon={<Add />}
            className={commonStyles.uppercase}
            onClick={handleAddItemModalOpen}
            disabled={disabled}
          >
            {addNewSubListItemButtonName}
          </Button>
        </Grid>
        <Grid {...defaultGridItemProps}>
          <Table columns={columns} list={list} loading={loading} />
        </Grid>
      </Grid>
      <AddSublistItemModal
        onClose={handleAddItemModalClose}
        open={addItemModalOpen}
        questions={sublistQuestions || []}
        fieldName={fieldName}
      />
    </>
  );
};
export default SublistQuestionFormItem;
